// hooks/use-scroll-lock.ts
import { useEffect } from "react";

// 1. Global counter keeps track of how many active locks exist across the entire app
let lockCount = 0;

export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;

    lockCount++;

    // Only apply the CSS freeze if this is the FIRST component requesting a lock
    if (lockCount === 1) {
      const html = document.documentElement;
      const body = document.body;

      // 2. Calculate the scrollbar width so the page doesn't shift horizontally when it disappears
      const scrollbarWidth = window.innerWidth - html.clientWidth;
      const scrollY = window.scrollY;

      // Save the original styles dynamically so we don't overwrite user defaults
      html.dataset.originalOverflow = html.style.overflow || "";
      body.dataset.originalOverflow = body.style.overflow || "";
      body.dataset.originalPaddingRight = body.style.paddingRight || "";

      // Save original iOS specific styles
      body.dataset.originalPosition = body.style.position || "";
      body.dataset.originalTop = body.style.top || "";
      body.dataset.originalWidth = body.style.width || "";

      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }

      const isIOS =
        typeof window !== "undefined" &&
        /iPad|iPhone|iPod/.test(navigator.userAgent);

      // 3. The Magic Fix for `position: sticky`
      // `overflow: hidden` creates a new scroll context which breaks sticky elements.
      // `overflow: clip` stops the scroll exactly the same way, but preserves the viewport context,
      // keeping your sidebar perfectly stuck in place.
      if (isIOS) {
        // iOS Hack: Physically pin the body in place to stop touch-dragging
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.width = "100%";
      } else if (
        typeof CSS !== "undefined" &&
        CSS.supports("overflow", "clip")
      ) {
        html.style.overflow = "clip";
        body.style.overflow = "clip";
      } else {
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
      }
    }

    // Cleanup when the component unmounts or active becomes false
    return () => {
      lockCount--;

      // Only restore the scroll if NO OTHER components are currently locking it
      if (lockCount === 0) {
        const html = document.documentElement;
        const body = document.body;
        const isIOS =
          typeof window !== "undefined" &&
          /iPad|iPhone|iPod/.test(navigator.userAgent);

        let scrollY = 0;
        if (isIOS) {
          // Retrieve the scroll position before we wipe the top style
          scrollY = parseInt(body.style.top || "0", 10) * -1;
        }

        html.style.overflow = html.dataset.originalOverflow || "";
        body.style.overflow = body.dataset.originalOverflow || "";
        body.style.paddingRight = body.dataset.originalPaddingRight || "";

        body.style.position = body.dataset.originalPosition || "";
        body.style.top = body.dataset.originalTop || "";
        body.style.width = body.dataset.originalWidth || "";

        if (isIOS) {
          // Jump the user back to exactly where they were
          window.scrollTo(0, scrollY);
        }
      }
    };
  }, [active]);
}
